import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Debug logging (disabled in production)
const DEBUG = process.env.NODE_ENV === 'development';
const log = (...args) => DEBUG && console.log(...args);
const warn = (...args) => DEBUG && console.warn(...args);

// WebSocket reconnection config
const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_MAX_RECONNECT_ATTEMPTS = 5;
const WS_INITIAL_DELAY = 500; // Initial connection delay
const WS_PING_INTERVAL = 25000; // 25 seconds heartbeat

const TOKEN_KEY = 'bourbon_token';

// Safari private mode / restricted storage can throw on access
const safeStorage = {
  get: () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  set: (v) => { try { localStorage.setItem(TOKEN_KEY, v); } catch { /* ignore */ } },
  remove: () => { try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } },
};

const authError = (error, fallback) => {
  if (error.response?.data?.detail) {
    const d = error.response.data.detail;
    return typeof d === 'string' ? d : (d[0]?.msg || fallback);
  }
  if (error.response) return `${fallback} (erreur serveur ${error.response.status})`;
  return `${fallback} (serveur injoignable)`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start with true to check auth on mount
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(() => safeStorage.get());
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const [wsReconnecting, setWsReconnecting] = useState(false);
  
  // Refs for WebSocket management
  const wsRef = useRef(null);
  const wsTimeoutRef = useRef(null);
  const wsReconnectTimeoutRef = useRef(null);
  const wsPingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const tokenRef = useRef(safeStorage.get());

  // Bearer token fallback: used when HttpOnly cookies are blocked by the browser
  const storeToken = useCallback((accessToken) => {
    tokenRef.current = accessToken || null;
    setToken(accessToken || null);
    if (accessToken) safeStorage.set(accessToken);
    else safeStorage.remove();
  }, []);

  // Axios instance with credentials (cookies) + Bearer fallback
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
      withCredentials: true, // Essential for HttpOnly cookies
      headers: { 'Content-Type': 'application/json' }
    });

    instance.interceptors.request.use((config) => {
      if (tokenRef.current && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });
    
    // Response interceptor for token refresh
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // If 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // Try to refresh the token
            const { data } = await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
            if (data?.access_token) {
              tokenRef.current = data.access_token;
              safeStorage.set(data.access_token);
              originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
            }
            // Retry the original request
            return instance(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear auth state
            tokenRef.current = null;
            safeStorage.remove();
            setUser(null);
            setIsAuthenticated(false);
            return Promise.reject(error);
          }
        }
        
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, []);

  // Check auth on mount - cookies are sent automatically
  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
  }, [api]);

  // WebSocket connection function
  const connectWebSocket = useCallback((commune, userId) => {
    if (!isMountedRef.current || !commune || !userId) return;

    try {
      // Clear any pending reconnect to avoid race conditions
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
        wsReconnectTimeoutRef.current = null;
      }
      
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const wsEndpoint = `${wsUrl}/api/ws/${encodeURIComponent(commune)}?user_id=${userId}`;
      
      // Close existing connection properly
      if (wsRef.current) {
        const currentState = wsRef.current.readyState;
        if (currentState === WebSocket.OPEN || currentState === WebSocket.CONNECTING) {
          wsRef.current.close(1000, 'Reconnecting');
        }
      }
      
      wsRef.current = new WebSocket(wsEndpoint);
      
      wsRef.current.onopen = () => {
        if (!isMountedRef.current) return;
        log('[WS] Connected to', commune);
        setWsConnected(true);
        setWsReconnecting(false);
        reconnectAttemptsRef.current = 0;
        
        // Start heartbeat to keep connection alive
        if (wsPingIntervalRef.current) {
          clearInterval(wsPingIntervalRef.current);
        }
        wsPingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
          }
        }, WS_PING_INTERVAL);
      };
      
      wsRef.current.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'notification') {
            setNotifications(prev => [data.data, ...prev]);
            setUnreadCount(prev => prev + 1);
          } else if (data.type === 'new_message') {
            // New message received - increment unread messages count
            setUnreadMessagesCount(prev => prev + 1);
          } else if (data.type === 'new_post' && data.post) {
            window.dispatchEvent(new CustomEvent('bourbon:new_post', { detail: data.post }));
          } else if (data.type === 'pong') {
            // Heartbeat response
          }
        } catch (e) {
          warn('[WS] Error parsing message:', e);
        }
      };
      
      wsRef.current.onerror = () => {
        if (!isMountedRef.current) return;
        warn('[WS] Connection error');
        setWsConnected(false);
      };
      
      wsRef.current.onclose = (event) => {
        if (!isMountedRef.current) return;
        log('[WS] Connection closed:', event.code, event.reason);
        setWsConnected(false);
        
        // Clear ping interval
        if (wsPingIntervalRef.current) {
          clearInterval(wsPingIntervalRef.current);
          wsPingIntervalRef.current = null;
        }
        
        // Auto-reconnect if not a clean close and user is still logged in
        if (event.code !== 1000 && event.code !== 1001 && user && reconnectAttemptsRef.current < WS_MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          setWsReconnecting(true);
          console.log(`[WS] Reconnecting in ${WS_RECONNECT_DELAY}ms (attempt ${reconnectAttemptsRef.current}/${WS_MAX_RECONNECT_ATTEMPTS})`);
          
          wsReconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current && user?.commune && user?.user_id) {
              connectWebSocket(user.commune, user.user_id);
            }
          }, WS_RECONNECT_DELAY);
        } else if (reconnectAttemptsRef.current >= WS_MAX_RECONNECT_ATTEMPTS) {
          warn('[WS] Max reconnection attempts reached');
          setWsReconnecting(false);
        }
      };
    } catch (error) {
      warn('[WS] Failed to establish connection:', error);
      setWsConnected(false);
    }
  }, [user]);

  // WebSocket connection effect with optimized cleanup
  useEffect(() => {
    isMountedRef.current = true;
    
    if (user?.commune && user?.user_id) {
      // Clear any existing timeouts
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
      }
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
      }
      
      // Add initial delay to ensure auth is fully established
      wsTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          connectWebSocket(user.commune, user.user_id);
        }
      }, WS_INITIAL_DELAY);
    }

    return () => {
      isMountedRef.current = false;
      
      // Clear all timeouts
      if (wsTimeoutRef.current) {
        clearTimeout(wsTimeoutRef.current);
        wsTimeoutRef.current = null;
      }
      if (wsReconnectTimeoutRef.current) {
        clearTimeout(wsReconnectTimeoutRef.current);
        wsReconnectTimeoutRef.current = null;
      }
      
      // Clear ping interval
      if (wsPingIntervalRef.current) {
        clearInterval(wsPingIntervalRef.current);
        wsPingIntervalRef.current = null;
      }
      
      // Close WebSocket only if it's open or connecting
      if (wsRef.current) {
        const state = wsRef.current.readyState;
        if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
          wsRef.current.close(1000, 'Component unmounted');
        }
        wsRef.current = null;
      }
      
      setWsConnected(false);
      setWsReconnecting(false);
    };
  }, [user?.user_id, user?.commune, connectWebSocket]);

  // Fetch unread count - optimized dependency
  useEffect(() => {
    if (user?.user_id) {
      fetchUnreadCount();
      fetchUnreadMessagesCount();
    }
  }, [user?.user_id]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/unread/count');
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchUnreadMessagesCount = async () => {
    try {
      const response = await api.get('/messages/unread/count');
      setUnreadMessagesCount(response.data.count);
    } catch (error) {
      console.error('Error fetching unread messages count:', error);
    }
  };

  // Fetch all unread counts (notifications + messages)
  const refreshUnreadCounts = useCallback(async () => {
    await Promise.all([fetchUnreadCount(), fetchUnreadMessagesCount()]);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      setUser(response.data);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/login', { email: email.trim(), password });
      const { user: userData, access_token } = response.data;
      storeToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on new login
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: authError(error, 'Erreur de connexion') };
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name, commune) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/register', { email: email.trim(), password, name: name.trim(), commune });
      const { user: userData, access_token } = response.data;
      storeToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      reconnectAttemptsRef.current = 0;
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: authError(error, 'Erreur d\'inscription') };
    } finally {
      setLoading(false);
    }
  };

  const processGoogleSession = async (sessionId) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/session', { session_id: sessionId });
      const { user: userData, access_token } = response.data;
      storeToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);
      reconnectAttemptsRef.current = 0;
      return { success: true, user: userData };
    } catch (error) {
      return { success: false, error: authError(error, 'Erreur d\'authentification Google') };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    // Close WebSocket cleanly before logout
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
        wsRef.current.close(1000, 'User logout');
      }
    }
    
    try {
      // Server will clear HttpOnly cookies
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state (cookies are cleared by server)
      storeToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setNotifications([]);
      setUnreadCount(0);
      setUnreadMessagesCount(0);
      setWsConnected(false);
      setWsReconnecting(false);
      reconnectAttemptsRef.current = 0;
    }
  };

  const updateUser = async (userData) => {
    try {
      const response = await api.put('/users/me', userData);
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur de mise à jour' };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return { success: true, user: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Erreur de rafraîchissement' };
    }
  };

  const markNotificationsRead = async () => {
    try {
      await api.post('/notifications/mark-read');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking notifications read:', error);
    }
  };

  // Manual reconnect function (exposed to UI)
  const reconnectWebSocket = useCallback(() => {
    if (user?.commune && user?.user_id) {
      reconnectAttemptsRef.current = 0;
      connectWebSocket(user.commune, user.user_id);
    }
  }, [user?.commune, user?.user_id, connectWebSocket]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    setUser,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    checkAuth,
    processGoogleSession,
    updateUser,
    refreshUser,
    notifications,
    unreadCount,
    unreadMessagesCount,
    fetchUnreadCount,
    fetchUnreadMessagesCount,
    refreshUnreadCounts,
    markNotificationsRead,
    api,
    wsConnected,
    wsReconnecting,
    reconnectWebSocket
  }), [
    user, token, isAuthenticated, loading, notifications, unreadCount, unreadMessagesCount,
    wsConnected, wsReconnecting, login, register, logout, checkAuth,
    processGoogleSession, updateUser, refreshUser, fetchUnreadCount,
    fetchUnreadMessagesCount, refreshUnreadCounts, markNotificationsRead,
    api, reconnectWebSocket
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
